import { useEffect, useState } from "react";
import { Outlet, NavLink, useNavigate } from "react-router-dom";
import { SquaresFour, Users, Receipt, IdentificationBadge, MapPin, ChartLine, SignOut, DeviceMobile, Warning, QrCode, Storefront, ShieldCheck, Coins } from "@phosphor-icons/react";
import Logo from "@/components/Logo";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { initials } from "@/lib/format";

const nav = [
  { to: "/business", icon: SquaresFour, label: "Dashboard", end: true },
  { to: "/business/profile", icon: Storefront, label: "Business Profile" },
  { to: "/business/customers", icon: Users, label: "Customers" },
  { to: "/business/engine", icon: Coins, label: "Loyalty Engine" },
  { to: "/business/transactions", icon: Receipt, label: "Transactions" },
  { to: "/business/staff", icon: IdentificationBadge, label: "Staff" },
  { to: "/business/branches", icon: MapPin, label: "Branches" },
  { to: "/business/qr", icon: QrCode, label: "QR Codes" },
  { to: "/business/reports", icon: ChartLine, label: "Reports" },
  { to: "/business/security", icon: ShieldCheck, label: "Security" },
];

const linkClass = ({ isActive }) => `flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm transition-colors ${isActive ? "bg-primary/15 text-primary font-semibold" : "text-muted-foreground hover:bg-black/5"}`;

export default function BusinessLayout() {
  const { logout, user } = useAuth();
  const navigate = useNavigate();
  const [biz, setBiz] = useState(null);

  useEffect(() => {
    api.get("/business/me").then((r) => setBiz(r.data)).catch(() => {});
  }, []);

  return (
    <div className="min-h-screen ok-bg grain relative flex">
      <aside className="hidden md:flex w-64 shrink-0 flex-col border-r border-black/8 p-5 sticky top-0 h-screen">
        <div className="flex items-center gap-2 mb-8">
          <Logo className="h-7" />
          <span className="text-muted-foreground font-normal text-sm">Business</span>
        </div>
        <nav className="flex-1 space-y-1 overflow-y-auto no-scrollbar">
          {nav.map((n) => (
            <NavLink key={n.to} to={n.to} end={n.end} data-testid={`biz-nav-${n.label.toLowerCase().replace(/\s+/g, "-")}`} className={linkClass}>
              <n.icon size={20} weight="duotone" /> {n.label}
            </NavLink>
          ))}
        </nav>
        <button onClick={() => navigate("/staff")} className="flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm text-muted-foreground hover:bg-black/5 mb-1">
          <DeviceMobile size={20} /> Staff mode
        </button>
        <button data-testid="biz-logout" onClick={() => { logout(); navigate("/login"); }} className="flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm text-muted-foreground hover:bg-black/5">
          <SignOut size={20} /> Sign out
        </button>
      </aside>

      <div className="flex-1 min-w-0 flex flex-col">
        <header className="glass sticky top-0 z-10 flex items-center justify-between px-4 sm:px-6 py-3 sm:py-4">
          <div className="flex items-center gap-3">
            <div className="h-9 w-9 rounded-xl bg-primary/15 flex items-center justify-center">
              <span className="font-display font-bold text-primary text-sm">{initials(biz?.name)}</span>
            </div>
            <div>
              <p className="font-display font-semibold text-sm">{biz?.name || "Loading…"}</p>
              <p className="text-[10px] text-muted-foreground">{user?.name} · {user?.role?.replace("_", " ")}</p>
            </div>
          </div>
          {biz && biz.status !== "active" && (
            <span data-testid="biz-status-badge" className="flex items-center gap-1.5 text-xs text-amber-600 bg-amber-500/10 border border-amber-500/30 rounded-full px-3 py-1.5">
              <Warning size={14} /> {biz.status === "pending" ? "Pending approval" : biz.status}
            </span>
          )}
        </header>
        <main className="flex-1 p-4 sm:p-6 max-w-6xl w-full pb-24 md:pb-6">
          <Outlet context={biz} />
        </main>
        {/* mobile nav */}
        <nav className="md:hidden glass fixed bottom-0 inset-x-0 flex overflow-x-auto no-scrollbar px-2 py-2 gap-1">
          {nav.map((n) => (
            <NavLink key={n.to} to={n.to} end={n.end} className={({ isActive }) => `flex flex-col items-center gap-0.5 px-3 py-1 rounded-lg text-[10px] shrink-0 ${isActive ? "text-primary" : "text-muted-foreground"}`}>
              <n.icon size={18} /> {n.label}
            </NavLink>
          ))}
        </nav>
      </div>
    </div>
  );
}
